'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ReactFlowProvider } from 'reactflow';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '../stores/workflowStore';
import { useWorkflowListStore, SavedWorkflow } from '../stores/workflowListStore';
import { useUploadedFileStore } from '../stores/uploadedFileStore';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { useTeamStore } from '../stores/teamStore';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { useToastActions } from '../components/ui/Toast';
import {
  Play,
  FloppyDisk,
  Trash,
  X,
  Check,
  Robot,
  ClockCounterClockwise,
  Crown,
  GitCommit,
} from '@phosphor-icons/react';
import { DataArea } from '../components/workflow/DataArea';
import { AIDrawer } from '../components/workflow/AIDrawer';
import { NodePanel } from '../components/workflow/NodePanel';
import { WorkflowCanvas } from '../components/workflow/WorkflowCanvas';
import { PropertyPanel } from '../components/workflow/PropertyPanel';
import { VersionHistoryDrawer } from '../components/workflow/VersionHistoryDrawer';
import { ChangeHistoryDrawer } from '../components/workflow/ChangeHistoryDrawer';
import { ExportDialog, ImportDialog } from '../components/workflow/ExportImportDialog';
import { useSchema } from '../contexts/SchemaContext';
import { workflowApi } from '../services/api';
import { getNodeDisplayLabel } from '../utils/nodeDisplayLabel';
// import { useConfetti } from '../hooks/useConfetti';
import type { NodeDSL, EdgeDSL, WorkflowVisibility } from '../types';
import type { FlowNode, FlowEdge } from '../stores/workflowStore';

// ── Save Dialog ──────────────────────────────────────────────────────────────
function SaveDialog({
  isOpen,
  onClose,
  onSave,
  initialName,
  initialDescription,
  initialVisibility,
  isEdit,
  hasTeam,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, description: string, status: 'draft' | 'active', visibility: WorkflowVisibility, changeSummary: string) => void;
  initialName: string;
  initialDescription: string;
  initialVisibility?: WorkflowVisibility;
  isEdit: boolean;
  hasTeam: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [status, setStatus] = useState<'draft' | 'active'>('active');
  const [visibility, setVisibility] = useState<WorkflowVisibility>(initialVisibility || 'private');
  const [changeSummary, setChangeSummary] = useState('');

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setDescription(initialDescription);
      setVisibility(initialVisibility || 'private');
      setChangeSummary('');
    }
  }, [isOpen, initialName, initialDescription, initialVisibility]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                <FloppyDisk className="w-5 h-5 text-emerald-600" weight="fill" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900">
                  {isEdit ? t('workflow.saveChanges') : t('workflow.saveWorkflow')}
                </h3>
                <p className="text-xs text-zinc-500">
                  {isEdit ? t('workflow.updateWorkflowInfo') : t('workflow.saveWorkflowDesc')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <Input
              label={t('workflow.workflowName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('workflow.workflowNamePlaceholder')}
              required
            />
            <div className="space-y-2">
              <label className="block text-sm font-medium text-zinc-700">{t('workflow.description')}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('workflow.descriptionPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all resize-none"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-zinc-700">{t('workflow.status')}</label>
              <div className="flex gap-2">
                {(['active', 'draft'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-all ${
                      status === s
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'
                    }`}
                  >
                    {s === 'active' ? t('workflow.statusEnable') : t('workflow.statusDraft')}
                  </button>
                ))}
              </div>
            </div>
            {/* 可见性选择 - 始终显示 */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-zinc-700">{t('workflow.visibility') || '可见性'}</label>
              <div className="flex gap-2">
                {/* 仅自己可见 - 始终可选 */}
                <button
                  onClick={() => setVisibility('private')}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-all ${
                    visibility === 'private'
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'
                  }`}
                >
                  {t('workflow.visibilityPrivate') || '仅自己'}
                </button>
                {/* 团队可见 - 只在有团队时可选 */}
                <button
                  onClick={() => hasTeam && setVisibility('team')}
                  disabled={!hasTeam}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-all ${
                    visibility === 'team'
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'
                  } ${!hasTeam ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={!hasTeam ? (t('workflow.visibilityTeamDisabled') || '请先加入或创建一个团队') : ''}
                >
                  {t('workflow.visibilityTeam') || '团队'}
                </button>
              </div>
              <p className="text-xs text-zinc-500">
                {visibility === 'private' 
                  ? (t('workflow.visibilityPrivateDesc') || '仅您自己可以查看和使用此工作流')
                  : (t('workflow.visibilityTeamDesc') || '团队成员可以查看和使用此工作流，但只有管理员和编辑者可以修改')}
              </p>
            </div>
            
            {/* 修改说明 - 仅在编辑模式下显示 */}
            {isEdit && (
              <div className="space-y-2 pt-2 border-t border-zinc-100">
                <label className="block text-sm font-medium text-zinc-700">{t('workflow.changeSummary')}</label>
                <textarea
                  value={changeSummary}
                  onChange={(e) => setChangeSummary(e.target.value)}
                  placeholder={t('workflow.changeSummaryPlaceholder')}
                  rows={2}
                  className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all resize-none"
                />
              </div>
            )}
          </div>

          <div className="px-6 py-4 bg-zinc-50/50 border-t border-zinc-100 flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              onClick={() => onSave(name, description, status, visibility, changeSummary)}
              disabled={!name.trim()}
              leftIcon={<Check className="w-4 h-4" />}
            >
              {isEdit ? t('workflow.saveChanges') : t('workflow.saveWorkflow')}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── TopBar ───────────────────────────────────────────────────────────────────
function TopBar({
  workflowName,
  isEditMode,
  onSave,
  onRun,
  onClear,
  clearDisabled,
  isRunning,
  hasFile,
  onOpenAI,
  onTitleChange,
  onOpenVersionHistory,
  onOpenChangeHistory,
}: {
  workflowName?: string;
  isEditMode: boolean;
  onSave: () => void;
  onRun: () => void;
  onClear: () => void;
  /** 为 true 时禁用清空按钮（工作流无节点时） */
  clearDisabled?: boolean;
  isRunning: boolean;
  hasFile: boolean;
  onOpenAI: () => void;
  onTitleChange?: (newName: string) => void;
  onOpenVersionHistory?: () => void;
  onOpenChangeHistory?: () => void;
  onExport?: () => void;
  onImport?: () => void;
}) {
  const { t } = useTranslation();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = () => {
    if (!isEditMode || !onTitleChange) return;
    setEditValue(workflowName || '');
    setIsEditingTitle(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const finishEditing = () => {
    if (!isEditingTitle) return;
    setIsEditingTitle(false);
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== workflowName && onTitleChange) {
      onTitleChange(trimmed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      finishEditing();
    }
    if (e.key === 'Escape') {
      setEditValue(workflowName || '');
      setIsEditingTitle(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="h-12 flex-shrink-0 flex items-center gap-2 px-4 bg-white/95 backdrop-blur-xl border-b border-zinc-200/80 shadow-[0_1px_0_0_rgba(0,0,0,0.04)]">
      {/* Logo + title */}
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-7 h-7 rounded-lg bg-zinc-900 flex items-center justify-center flex-shrink-0 overflow-hidden">
          <img src="/logo.svg" alt="Logo" className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          {isEditingTitle ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={finishEditing}
              onKeyDown={handleKeyDown}
              className="text-sm font-semibold text-zinc-900 leading-tight w-full max-w-[180px] px-1.5 py-0.5 -ml-1 rounded border border-emerald-300 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              placeholder={t('workflow.workflowName')}
            />
          ) : (
            <h1
              className={`text-sm font-semibold text-zinc-900 leading-tight truncate max-w-[180px] ${isEditMode && onTitleChange ? 'cursor-pointer hover:text-emerald-600' : ''}`}
              onDoubleClick={startEditing}
              title={isEditMode && onTitleChange ? t('workflow.editName') : undefined}
            >
              {isEditMode && workflowName ? workflowName : t('workflow.newWorkflow')}
            </h1>
          )}
          <p className="text-[10px] text-zinc-400 leading-none">
            {isEditMode ? t('workflow.editMode') : t('workflow.createMode')}
          </p>
        </div>
      </div>

      {/* AI Assistant button */}
      <motion.button
        onClick={onOpenAI}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors select-none"
      >
        <Robot className="w-3.5 h-3.5" weight="fill" />
        {t('workflow.aiAssistant')}
      </motion.button>

      {isEditMode && onOpenVersionHistory && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenVersionHistory}
          leftIcon={<ClockCounterClockwise className="w-3.5 h-3.5" />}
        >
          {t('workflow.versionHistory')}
        </Button>
      )}

      {isEditMode && onOpenChangeHistory && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenChangeHistory}
          leftIcon={<GitCommit className="w-3.5 h-3.5" />}
        >
          {t('workflow.changeHistory')}
        </Button>
      )}

      <div className="flex-1" />

      {/* Action Buttons */}
      <div className="flex items-center gap-1.5">
        {/* onImport && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onImport}
            leftIcon={<Upload className="w-3.5 h-3.5" />}
          >
            {t('workflow.import.label')}
          </Button>
        ) */}
        {/* isEditMode && onExport && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onExport}
            leftIcon={<Download className="w-3.5 h-3.5" />}
          >
            {t('workflow.export.label')}
          </Button>
        )*/}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          disabled={clearDisabled}
          leftIcon={<Trash className="w-3.5 h-3.5" />}
        >
          {t('common.clear')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onSave}
          leftIcon={<FloppyDisk className="w-3.5 h-3.5" />}
        >
          {t('common.save')}
        </Button>
        <Button
          variant="accent"
          size="sm"
          onClick={onRun}
          isLoading={isRunning}
          disabled={!hasFile}
          leftIcon={!isRunning ? <Play className="w-3.5 h-3.5" weight="fill" /> : undefined}
        >
          {t('common.run')}
        </Button>
      </div>
    </div>
  );
}

// ── Resize Handle ────────────────────────────────────────────────────────────
function ResizeHandle({ onMouseDown }: { onMouseDown: (e: React.MouseEvent) => void }) {
  const { t } = useTranslation();
  return (
    <div
      className="h-1.5 flex-shrink-0 bg-zinc-100 hover:bg-emerald-50 border-y border-zinc-200 cursor-row-resize flex items-center justify-center group transition-colors duration-150 select-none"
      onMouseDown={onMouseDown}
      title={t('workflow.resizeHint')}
    >
      <div className="w-14 h-0.5 rounded-full bg-zinc-300 group-hover:bg-emerald-400 transition-colors duration-150" />
    </div>
  );
}

// ── Main Editor ───────────────────────────────────────────────────────────────
function WorkflowEditor() {
  const { t, i18n } = useTranslation();
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const isEditMode = !!workflowId;

  const { nodes, edges, addNode, addEdge, clearWorkflow, loadWorkflow, selectedNode } = useWorkflowStore();
  const { getWorkflow, addWorkflow, updateWorkflow, updateWorkflowLocal, fetchWorkflows, incrementExecutionCount } = useWorkflowListStore();
  // Per-node file state lives here (keyed by nodeId)
  const { files, getOriginalExcelFile, setUploadedFile: setStoreFile, clearAllFiles } = useUploadedFileStore();
  const { subscription } = useSubscriptionStore();
  const { currentTeamId } = useTeamStore();
  const canExportImport = subscription?.plan !== 'basic' && subscription?.plan !== 'free';
  const hasTeam = !!currentTeamId;

  const [isRunning, setIsRunning] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isAIDrawerOpen, setIsAIDrawerOpen] = useState(false);
  const [isVersionDrawerOpen, setIsVersionDrawerOpen] = useState(false);
  const [isChangeHistoryDrawerOpen, setIsChangeHistoryDrawerOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isExportImportUpgradeOpen, setIsExportImportUpgradeOpen] = useState(false);
  const [executionResults, setExecutionResults] = useState<{
    nodeId: string;
    label: string;
    data: Record<string, unknown>[];
    columns: string[];
    rowCount: number;
    metadata?: Record<string, unknown>;
    isDiffResult?: boolean;
    excelStyles?: import('../services/api').ExcelStylesPayload;
    executionId?: string;
  }[]>([]);
  const [executionLogs, setExecutionLogs] = useState<string[]>([]);
  const [currentWorkflowName, setCurrentWorkflowName] = useState('');
  const [currentDescription, setCurrentDescription] = useState('');
  const [currentWorkflow, setCurrentWorkflow] = useState<SavedWorkflow | null>(null);

  // 未保存提醒：记录加载时的快照，用于判断是否有未保存修改
  const initialSnapshotRef = useRef<{
    nodeCount: number;
    edgeCount: number;
    name: string;
    description: string;
  } | null>(null);
  /** 保存后即将跳转，此时不再弹出「未保存」确认框 */
  const navigatingAfterSaveRef = useRef(false);

  // 新建模式下，首次进入时没有快照：用“是否有内容”做粗略判断；
  // 一旦保存成功写入快照后，后续与编辑模式一致：对比快照判断是否有未保存修改。
  const isDirty =
    initialSnapshotRef.current == null
      ? (!isEditMode && (nodes.length > 0 || edges.length > 0 || currentWorkflowName.trim() !== ''))
      : nodes.length !== initialSnapshotRef.current.nodeCount ||
        edges.length !== initialSnapshotRef.current.edgeCount ||
        currentWorkflowName !== initialSnapshotRef.current.name ||
        currentDescription !== initialSnapshotRef.current.description;

  // Vertical split state
  const [topPercent, setTopPercent] = useState(58);
  const [isOutputCollapsed, setIsOutputCollapsed] = useState(false);
  const [topPercentBeforeCollapse, setTopPercentBeforeCollapse] = useState(58);
  const containerRef = useRef<HTMLDivElement>(null);

  const { confirm, dialog: confirmDialog } = useConfirm();
  const toast = useToastActions();
  const { clearAllSchemas, registerNodeSchema } = useSchema();
  // const { fire: fireConfetti } = useConfetti();

  // Resize drag handler
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;

      const startY = e.clientY;
      const startPercent = topPercent;
      const containerHeight = container.getBoundingClientRect().height;

      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';

      const onMove = (moveE: MouseEvent) => {
        const delta = moveE.clientY - startY;
        const deltaPercent = (delta / containerHeight) * 100;
        const next = Math.max(25, Math.min(80, startPercent + deltaPercent));
        setTopPercent(next);
      };

      const onUp = () => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [topPercent]
  );

  // Double-click handle to reset ratio
  const handleResizeDblClick = () => setTopPercent(58);

  // 离开页面前若未保存则提醒（刷新/关闭标签页）
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  // 应用内跳转时若未保存则确认（需使用 createBrowserRouter 时 useBlocker 才会生效）
  const blocker = useBlocker(isDirty);
  const blockerHandledRef = useRef(false);
  useEffect(() => {
    if (blocker.state !== 'blocked') {
      return;
    }
    if (navigatingAfterSaveRef.current) {
      navigatingAfterSaveRef.current = false;
      blocker.proceed();
      return;
    }
    if (blockerHandledRef.current) return;
    blockerHandledRef.current = true;
    confirm({
      title: t('workflow.unsavedTitle'),
      description: t('workflow.unsavedDescription'),
      confirmText: t('workflow.unsavedConfirm'),
      cancelText: t('workflow.unsavedCancel'),
      type: 'danger',
    })
      .then((ok) => {
        if (blocker.state === 'blocked') {
          if (ok) blocker.proceed();
          else blocker.reset();
        }
      })
      .finally(() => {
        // 推迟一帧再重置，确保 router 在 reset() 后完成状态更新，下次点击菜单能再次触发拦截与弹窗
        setTimeout(() => {
          blockerHandledRef.current = false;
        }, 0);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state]);

  // Load existing workflow in edit mode, or clear for new workflow
  useEffect(() => {
    if (isEditMode && workflowId) {
      const saved = getWorkflow(workflowId);
      if (saved) {
        setCurrentWorkflow(saved);
        setCurrentWorkflowName(saved.name);
        setCurrentDescription(saved.description);
        const fakeWorkflow = {
          id: saved.id,
          name: saved.name,
          description: saved.description,
          status: saved.status as 'active' | 'inactive' | 'draft',
          visibility: saved.visibility,
          teamId: saved.teamId,
          dag: {
            nodes: Object.fromEntries(
              saved.nodes.map((n) => [
                n.id,
                { id: n.id, type: n.type, config: n.data.config, position: n.position, inputs: [] },
              ])
            ),
            edges: saved.edges.map((e) => ({ from: e.source, to: e.target })),
          },
          createdAt: saved.createdAt,
          updatedAt: saved.updatedAt,
        };
        loadWorkflow(fakeWorkflow as Parameters<typeof loadWorkflow>[0]);

        if (saved.uploadedFile) {
          // Restore the previously saved file under the legacy key; actual
          // per-node data is not persisted (the user will re-upload if needed).
          setStoreFile({ ...saved.uploadedFile, data: [] });
        }
        initialSnapshotRef.current = {
          nodeCount: saved.nodes.length,
          edgeCount: saved.edges.length,
          name: saved.name,
          description: saved.description,
        };
      } else {
        toast.error(t('workflow.notFound'));
        navigate('/workflows');
        initialSnapshotRef.current = null;
      }
    } else {
      // 新建表格流模式：清空所有数据
      clearWorkflow();
      clearAllFiles();
      clearAllSchemas();
      setExecutionResults([]);
      setExecutionLogs([]);
      setCurrentWorkflowName('');
      setCurrentDescription('');
      initialSnapshotRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]);

  const INPUT_NODE_TYPES = new Set(['load_excel', 'load_csv', 'load_table', 'online_sheet']);

  /** Convert a file data record back to an XLSX File object for the API. */
  const buildXlsxFile = (fileData: { name: string; columns: string[]; data: Record<string, unknown>[] }): File => {
    const aoa = [fileData.columns, ...fileData.data.map((row) => fileData.columns.map((h) => row[h] ?? ''))];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const baseName = fileData.name.replace(/\.[^.]+$/, '');
    return new File([buf], `${baseName}.xlsx`, {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  };

  const handleRun = async () => {
    if (nodes.length === 0) {
      toast.error(t('workflow.noNodesError'));
      return;
    }

    // 如果输出面板处于折叠状态，自动展开
    if (isOutputCollapsed) {
      setIsOutputCollapsed(false);
      setTopPercent(topPercentBeforeCollapse);
    }

    // Collect all input nodes that have a file uploaded.
    // For load_excel, use original Excel file when available so server can preserve styles.
    const inputNodes = nodes.filter((n) => INPUT_NODE_TYPES.has(n.type ?? ''));
    const nodeFileEntries: Array<{ nodeId: string; file: File }> = [];
    for (const node of inputNodes) {
      const fd = files[node.id];
      if (!fd) continue;
      const params = (node.data?.config?.params ?? {}) as Record<string, unknown>;
      const isExcel =
        node.type === 'load_excel' ||
        (node.type === 'load_table' && params.tableFormat !== 'csv');
      const originalExcel = isExcel ? getOriginalExcelFile(node.id) : null;
      const fileToSend = originalExcel ?? buildXlsxFile(fd);
      nodeFileEntries.push({ nodeId: node.id, file: fileToSend });
    }

    if (nodeFileEntries.length === 0) {
      toast.error(t('workflow.noInputFilesError'));
      return;
    }

    // Warn if some input nodes are still missing files
    const missing = inputNodes.filter((n) => !files[n.id]);
    if (missing.length > 0) {
      toast.warning(
        t('workflow.missingFilesWarning', {
          nodes: missing.map((n) => getNodeDisplayLabel(n.type, n.data.label, t, i18n)).join('」「'),
        }),
      );
    }

    setIsRunning(true);
    setExecutionLogs([]);

    const logs: string[] = [];
    const addLog = (msg: string) => {
      const time = new Date().toLocaleTimeString();
      logs.push(`[${time}]  ${msg}`);
      setExecutionLogs([...logs]);
    };

    try {
      addLog(t('workflow.logs.start'));
      nodeFileEntries.forEach(({ nodeId }) => {
        const fd = files[nodeId]!;
        addLog(
          t('workflow.logs.dataSource', {
            id: nodeId,
            name: fd.name,
            rows: fd.rowCount.toLocaleString(),
          }),
        );
      });

      const workflowData = {
        name: currentWorkflowName || `SheetFlowing_${Date.now()}`,
        description: currentDescription || 'Temporary SheetFlow',
        nodes: nodes.map((n) => ({
          id: n.id,
          type: n.type,
          config: { ...(n.data?.config || { type: n.type, params: {} }) } as Record<string, unknown>,
          position: n.position,
        })),
        edges: edges.map((e) => ({ from: e.source, to: e.target })),
      };

      nodes.forEach((n) => {
        addLog(
          t('workflow.logs.nodeLoaded', {
            type: n.type,
          }),
        );
      });

      let targetWorkflowId: string;
      if (isEditMode && workflowId) {
        targetWorkflowId = workflowId;
        await workflowApi.updateWorkflowInStore(workflowId, workflowData);
      } else {
        const response = await workflowApi.createWorkflowInStore(workflowData);
        targetWorkflowId = (response as unknown as { data: { id: string } }).data.id;
      }

      addLog(t('workflow.logs.submitted'));

      const result = await workflowApi.runWorkflow(targetWorkflowId, nodeFileEntries, undefined, true);

      // ── Grouped result: one tab per group + one merged tab ──
      if (result.is_grouped && result.grouped_results && result.grouped_results.length > 0) {
        type ResultItem = {
          nodeId: string;
          label: string;
          data: Record<string, unknown>[];
          columns: string[];
          rowCount: number;
          excelStyles?: import('../services/api').ExcelStylesPayload;
          executionId?: string;
        };
        const groupItems: ResultItem[] = result.grouped_results.map((g, idx) => ({
          nodeId: `__group_${idx}__`,
          label: t('workflow.groupResult', { key: g.group_key }),
          data: g.rows,
          columns: g.columns,
          rowCount: g.rowCount,
          executionId: result.executionId,
        }));
        // Append a "合并结果" tab showing the flat union of all groups
        groupItems.push({
          nodeId: '__merged__',
          label: t('workflow.mergedResult'),
          data: result.rows,
          columns: result.columns,
          rowCount: result.rowCount,
          excelStyles: result.excelStyles,
          executionId: result.executionId,
        });
        setExecutionResults(groupItems);
        addLog(
          t('workflow.logs.groupDone', {
            groups: result.grouped_results.length,
            rows: result.rowCount.toLocaleString(),
          }),
        );
        
        // [已禁用] 自动下载合并结果
        // setTimeout(() => {
        //   autoDownloadResult(result.rows, result.columns, result.excelStyles);
        // }, 100);
      } else {
        // ── Single result (diff or regular) ──
        const isDiff = result.columns.includes('diff_type');
        setExecutionResults([
          {
            nodeId: '__result_0__',
            label: isDiff ? t('workflow.diffResult') : t('workflow.executionResult'),
            data: result.rows,
            columns: result.columns,
            rowCount: result.rowCount,
            isDiffResult: isDiff,
            excelStyles: result.excelStyles,
            executionId: result.executionId,
          },
        ]);
        addLog(
          t('workflow.logs.finished', {
            rows: result.rowCount.toLocaleString(),
          }),
        );
        
        // [已禁用] 自动下载结果（非差异对比类型）
        // if (!isDiff) {
        //   setTimeout(() => {
        //     autoDownloadResult(result.rows, result.columns, result.excelStyles);
        //   }, 100);
        //}
      }
      if (result.node_schemas) {
        for (const [nid, cols] of Object.entries(result.node_schemas)) {
          const node = nodes.find(n => n.id === nid);
          if (!node) continue;
          registerNodeSchema({
            nodeId: nid,
            nodeType: node.type ?? 'unknown',
            nodeName: getNodeDisplayLabel(node.type, node.data?.label as string | undefined, t, i18n),
            columns: cols.map((name, idx) => ({
              name,
              type: 'string' as const,
              nullable: true,
              index: idx,
            })),
          });
        }
      }

      toast.success(t('workflow.executionSuccess', { count: result.rowCount }));

      // 检查是否是第一次执行成功，如果是则触发庆祝礼花
      // const currentExecutionCount = isEditMode && workflowId
      //   ? (getWorkflow(workflowId)?.executionCount ?? 0)
      //   : 0;
      
      // if (currentExecutionCount === 0) {
      //   fireConfetti('first-workflow-execution', {
      //     particleCount: 120,
      //     spread: 70,
      //     fromBottom: true,
      //   });
      // }

      // 增加执行计数
      if (isEditMode && workflowId) {
        incrementExecutionCount(workflowId);
      }
    } catch (error) {
      console.error('Run workflow error:', error);
      const msg = error instanceof Error ? error.message : t('common.unknownError');
      addLog(
        t('workflow.logs.failed', {
          message: msg,
        }),
      );
      toast.error(msg);
    } finally {
      setIsRunning(false);
    }
  };

  const handleSave = () => setIsSaveDialogOpen(true);

  const handleTitleChange = async (newName: string) => {
    if (!workflowId || !newName.trim()) return;
    try {
      await updateWorkflow(workflowId, { name: newName.trim(), updatedAt: new Date().toISOString() });
      setCurrentWorkflowName(newName.trim());
      toast.success(t('workflow.nameSaved'));
    } catch (error) {
      console.error('Update workflow name error:', error);
      toast.error(error instanceof Error ? error.message : t('common.saveFailed'));
    }
  };

  const handleVersionRestored = async (wf: { nodes: NodeDSL[]; edges: EdgeDSL[]; name: string; description?: string }) => {
    if (!workflowId) return;
    const flowNodes: FlowNode[] = wf.nodes.map((n) => {
      const pos = n.position ?? { x: 0, y: 0 };
      return {
        id: n.id,
        type: n.type,
        position: { x: typeof pos.x === 'number' ? pos.x : 0, y: typeof pos.y === 'number' ? pos.y : 0 },
        data: {
          label: (n as { label?: string }).label?.trim() ?? '',
          config: (n.config?.params ? { type: n.type, params: n.config.params } : (n.config ?? { type: n.type, params: {} })) as unknown as FlowNode['data']['config'],
          status: 'idle',
        },
      };
    });
    const flowEdges: FlowEdge[] = wf.edges.map((e, i) => ({
      id: `e_${e.from}_${e.to}_${i}`,
      source: e.from,
      target: e.to,
    }));
    // 仅更新本地 store，不请求 API，避免恢复时产生新版本；新版本仅在用户点击保存时创建
    updateWorkflowLocal(workflowId, {
      nodes: flowNodes,
      edges: flowEdges,
      name: wf.name,
      description: wf.description ?? '',
      nodeCount: flowNodes.length,
      updatedAt: new Date().toISOString(),
    });
    const saved = getWorkflow(workflowId);
    if (saved) {
      setCurrentWorkflowName(saved.name);
      setCurrentDescription(saved.description);
      const fakeWorkflow = {
        id: saved.id,
        name: saved.name,
        description: saved.description,
        status: saved.status as 'active' | 'inactive' | 'draft',
        dag: {
          nodes: Object.fromEntries(
            saved.nodes.map((n) => [
              n.id,
              { id: n.id, type: n.type, config: n.data.config, position: n.position, inputs: [] },
            ])
          ),
          edges: saved.edges.map((e) => ({ from: e.source, to: e.target })),
        },
        createdAt: saved.createdAt,
        updatedAt: saved.updatedAt,
      };
      loadWorkflow(fakeWorkflow as Parameters<typeof loadWorkflow>[0]);
      initialSnapshotRef.current = {
        nodeCount: saved.nodes.length,
        edgeCount: saved.edges.length,
        name: saved.name,
        description: saved.description,
      };
    }
  };

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  const handleRunRef  = useRef(handleRun);
  const handleSaveRef = useRef(handleSave);
  handleRunRef.current  = handleRun;
  handleSaveRef.current = handleSave;

  useEffect(() => {
    const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
    const handler = (e: KeyboardEvent) => {
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (!mod) return;
      if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        handleSaveRef.current();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleRunRef.current();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []); // refs always up-to-date; no deps needed

  const handleSaveConfirm = async (name: string, description: string, status: 'draft' | 'active', visibility: WorkflowVisibility, changeSummary: string = '') => {
    const now = new Date().toISOString();
    const firstFile = Object.values(files)[0] ?? null;
    const savedFileRef = firstFile
      ? { id: firstFile.id, name: firstFile.name, rowCount: firstFile.rowCount, columns: firstFile.columns }
      : null;

    try {
      if (isEditMode && workflowId) {
        await updateWorkflow(workflowId, {
          name,
          description,
          status,
          visibility,
          nodes,
          edges,
          nodeCount: nodes.length,
          uploadedFile: savedFileRef,
          updatedAt: now,
          changeSummary,
        });
        setCurrentWorkflowName(name);
        setCurrentDescription(description);
        toast.success(t('workflow.saveSuccess'));
      } else {
        const created = await addWorkflow({
          id: '', // 服务端会返回 id
          name,
          description,
          status,
          visibility,
          nodes,
          edges,
          nodeCount: nodes.length,
          executionCount: 0,
          uploadedFile: savedFileRef,
          createdAt: now,
          updatedAt: now,
        });
        setCurrentWorkflowName(created.name);
        setCurrentDescription(created.description);
        toast.success(t('workflow.createSuccess'));
        // 保存后跳转到工作流编辑页（带 id），并跳过一次未保存拦截
        navigatingAfterSaveRef.current = true;
        navigate(`/workflows/store/edit/${created.id}`, { replace: true });
      }
      initialSnapshotRef.current = {
        nodeCount: nodes.length,
        edgeCount: edges.length,
        name,
        description,
      };
      setIsSaveDialogOpen(false);
    } catch (e) {
      console.error('Save workflow failed:', e);
      toast.error(t('workflow.saveFailed') ?? '保存失败');
    }
  };

  const handleClear = async () => {
    const confirmed = await confirm({
      title: t('workflow.clearConfirm'),
      description: t('workflow.clearDescription'),
      confirmText: t('common.confirm'),
      cancelText: t('common.cancel'),
      type: 'danger',
    });
    if (confirmed) {
      clearWorkflow();
      clearAllFiles();
      clearAllSchemas();
      setExecutionResults([]);
      setExecutionLogs([]);
      toast.success(t('workflow.clearSuccess'));
    }
  };

  // 自动下载结果（执行完成后调用）
  const autoDownloadResult = async (
    rows: Record<string, unknown>[],
    columns: string[],
    excelStyles?: import('../services/api').ExcelStylesPayload,
  ) => {
    // 查找表格流中的输出节点
    const exportNode = nodes.find(n => n.type === 'export');
    const nodeConfig = exportNode?.data?.config?.params || {};
    const exportFormat = (nodeConfig.format as 'excel' | 'csv') || 'excel';
    
    // 获取文件名（不含扩展名），默认为 'result'
    const baseFileName = nodeConfig.fileName || 'result';

    if (exportFormat === 'csv') {
      // CSV 导出
      const aoa: unknown[][] = [
        columns,
        ...rows.map((row) => columns.map((c) => row[c] ?? '')),
      ];
      const separator = (nodeConfig.separator as string) || ',';
      const encoding = (nodeConfig.encoding as string) || 'utf-8';
      
      const csvContent = aoa.map(row => 
        row.map(cell => {
          const str = String(cell ?? '');
          if (str.includes(separator) || str.includes('\n') || str.includes('"')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        }).join(separator)
      ).join('\n');

      let blob: Blob;
      if (encoding === 'utf-8') {
        blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: 'text/csv;charset=utf-8;' });
      } else {
        blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      }
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseFileName}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }

    // Excel 导出：有样式时走服务端导出以保留字体/颜色/背景等格式
    if (excelStyles) {
      try {
        const blob = await workflowApi.exportExcel({
          columns,
          rows,
          excelStyles,
          fileName: `${baseFileName}.xlsx`,
          sheetName: (nodeConfig.sheetName as string) || undefined,
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseFileName}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      } catch (err) {
        console.error('auto export-excel with styles failed, fallback to client XLSX', err);
      }
    }

    // 兜底：客户端 XLSX（无样式）
    const aoa: unknown[][] = [
      columns,
      ...rows.map((row) => columns.map((c) => row[c] ?? '')),
    ];
    const sheetName = (nodeConfig.sheetName as string) || 'Sheet1';
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseFileName}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadResult = async (
    index = 0,
    exportOverride?: { columns: string[]; rows: string[][] }
  ) => {
    const result = executionResults[index];
    if (!result) return;

    // 查找表格流中的输出节点
    const exportNode = nodes.find(n => n.type === 'export');
    const nodeConfig = exportNode?.data?.config?.params || {};
    const exportFormat = (nodeConfig.format as 'excel' | 'csv') || 'excel';
    
    // 获取文件名（不含扩展名），默认为 'result'
    const baseFileName = nodeConfig.fileName || 'result';

    // Excel 且无 exportOverride 且有样式时，走服务端导出以保留样式
    if (exportFormat === 'excel' && !exportOverride && result.excelStyles) {
      try {
        const blob = await workflowApi.exportExcel({
          columns: result.columns,
          rows: result.data,
          excelStyles: result.excelStyles,
          fileName: `${baseFileName}.xlsx`,
          sheetName: (nodeConfig.sheetName as string) || undefined,
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseFileName}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      } catch (err) {
        console.error('export-excel failed, fallback to client XLSX', err);
      }
    }

    // For diff results, use the export data built from original columns + cell decisions.
    // For regular results, use the raw columns/rows from the API response.
    let aoa: unknown[][];
    if (exportOverride) {
      aoa = [exportOverride.columns, ...exportOverride.rows];
    } else {
      aoa = [
        result.columns,
        ...result.data.map((row) => result.columns.map((c) => row[c] ?? '')),
      ];
    }

    if (exportFormat === 'csv') {
      // CSV 导出
      const separator = (nodeConfig.separator as string) || ',';
      const encoding = (nodeConfig.encoding as string) || 'utf-8';
      
      // 将数据转换为 CSV 格式
      const csvContent = aoa.map(row => 
        row.map(cell => {
          const str = String(cell ?? '');
          // 如果包含分隔符、换行符或引号，需要转义
          if (str.includes(separator) || str.includes('\n') || str.includes('"')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        }).join(separator)
      ).join('\n');

      // 处理编码
      let blob: Blob;
      if (encoding === 'utf-8') {
        blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: 'text/csv;charset=utf-8;' });
      } else {
        // 对于 GBK/GB2312，需要使用 TextEncoder 或其他方式
        // 这里使用简单的 UTF-8 作为后备
        blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      }
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseFileName}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      // Excel 导出
      const sheetName = (nodeConfig.sheetName as string) || 'Sheet1';
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseFileName}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  void addNode;
  void addEdge;
  void autoDownloadResult;

  return (
    <div className="h-full flex flex-col bg-zinc-50 overflow-hidden">
      {/* ── Top Bar ── */}
      <TopBar
        workflowName={currentWorkflowName}
        isEditMode={isEditMode}
        onSave={handleSave}
        onRun={handleRun}
        onClear={handleClear}
        clearDisabled={nodes.length === 0}
        isRunning={isRunning}
        hasFile={Object.keys(files).length > 0}
        onOpenAI={() => setIsAIDrawerOpen(true)}
        onTitleChange={isEditMode && workflowId ? handleTitleChange : undefined}
        onOpenVersionHistory={isEditMode && workflowId ? () => setIsVersionDrawerOpen(true) : undefined}
        onOpenChangeHistory={isEditMode && workflowId ? () => setIsChangeHistoryDrawerOpen(true) : undefined}
        onExport={isEditMode && workflowId ? (canExportImport ? () => setIsExportDialogOpen(true) : () => setIsExportImportUpgradeOpen(true)) : undefined}
        onImport={canExportImport ? () => setIsImportDialogOpen(true) : () => setIsExportImportUpgradeOpen(true)}
      />

      {/* ── Main vertical split area ── */}
      <div ref={containerRef} className="flex-1 flex flex-col overflow-hidden min-h-0">
        {/* ── Top workspace: NodePanel + Canvas + PropertyPanel ── */}
        <div
          style={{ height: `${topPercent}%` }}
          className="flex-shrink-0 flex overflow-hidden min-h-0"
        >
          {/* Left: Node Palette — manages its own width (collapsed / expanded) */}
          <NodePanel />

          {/* Center: ReactFlow Canvas */}
          <WorkflowCanvas />

          {/* Right: Node Properties Panel — slides in when a node is selected */}
          <AnimatePresence>
            {selectedNode && (
              <motion.div
                key="property-panel"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 288, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 260, damping: 28 }}
                className="flex-shrink-0 overflow-hidden"
              >
                <PropertyPanel className="w-72 h-full" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Resize Handle ── */}
        {!isOutputCollapsed && (
          <div onDoubleClick={handleResizeDblClick}>
            <ResizeHandle onMouseDown={handleResizeMouseDown} />
          </div>
        )}

        {/* ── Bottom Data Area ── */}
        <div 
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isOutputCollapsed ? 'h-9 flex-shrink-0' : 'flex-1 min-h-0'
          }`}
        >
          <DataArea
            executionResults={executionResults}
            executionLogs={executionLogs}
            onDownloadResult={handleDownloadResult}
            isCollapsed={isOutputCollapsed}
            workflowName={currentWorkflowName || ''}
            onToggleCollapse={() => {
              if (isOutputCollapsed) {
                // 展开：恢复之前的高度
                setIsOutputCollapsed(false);
                setTopPercent(topPercentBeforeCollapse);
              } else {
                // 折叠：保存当前高度并最大化画布
                setTopPercentBeforeCollapse(topPercent);
                setIsOutputCollapsed(true);
                setTopPercent(95); // 画布占据 95% 的高度
              }
            }}
          />
        </div>
      </div>

      {/* AI Drawer */}
      <AIDrawer
        isOpen={isAIDrawerOpen}
        onClose={() => setIsAIDrawerOpen(false)}
        onWorkflowGenerated={() => toast.success(t('workflow.aiGeneratedSuccess'))}
      />

      {/* Save Dialog */}
      <VersionHistoryDrawer
        isOpen={isVersionDrawerOpen}
        onClose={() => setIsVersionDrawerOpen(false)}
        workflowId={workflowId!}
        workflowName={currentWorkflowName || ''}
        onRestored={handleVersionRestored}
      />

      {/* Change History Drawer */}
      <ChangeHistoryDrawer
        isOpen={isChangeHistoryDrawerOpen}
        onClose={() => setIsChangeHistoryDrawerOpen(false)}
        workflowId={workflowId!}
        workflowName={currentWorkflowName || ''}
      />
      <SaveDialog
        isOpen={isSaveDialogOpen}
        onClose={() => setIsSaveDialogOpen(false)}
        onSave={handleSaveConfirm}
        initialName={currentWorkflowName}
        initialDescription={currentDescription}
        initialVisibility={currentWorkflow?.visibility || 'private'}
        isEdit={isEditMode}
        hasTeam={hasTeam}
      />

      {/* Export Dialog */}
      <ExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        workflowId={workflowId!}
        workflowName={currentWorkflowName}
      />

      {/* Import Dialog */}
      <ImportDialog
        isOpen={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        onImportSuccess={async (workflow) => {
          // 导入成功后先刷新列表缓存，再跳转到编辑页面，避免 workflow.notFound
          try {
            await fetchWorkflows();
          } catch (e) {
            // 即使刷新失败也允许继续跳转（编辑页可由后续机制兜底）
            console.warn('fetchWorkflows after import failed:', e);
          }
          // 路由约定：/workflows/edit/:workflowId（再重定向到 /workflows/store/edit/:workflowId）
          navigate(`/workflows/edit/${workflow.id}`);
        }}
      />

      {/* 导出/导入需升级 Pro 提示 */}
      <AnimatePresence>
        {isExportImportUpgradeOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setIsExportImportUpgradeOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-amber-600" weight="fill" />
                </div>
                <p className="text-sm font-medium text-zinc-900">{t('workflow.exportImportUpgradeRequired')}</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setIsExportImportUpgradeOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={() => { setIsExportImportUpgradeOpen(false); navigate('/upgrade'); }}>
                  {t('nav.subscriptionUpgrade') || t('common.upgrade') || '升级'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {confirmDialog}
    </div>
  );
}

interface WorkflowEditorPageProps {
  mode?: 'home-store' | 'workflow-store';
}

export function WorkflowEditorPage({ mode }: WorkflowEditorPageProps) {
  void mode;
  return (
    <ReactFlowProvider>
      <WorkflowEditor />
    </ReactFlowProvider>
  );
}
